import { createChildBridge, ChannelNameSpace, CompsNameSpace } from './';
import { Utils } from '../InteractComps/utils';
import { classTracker, trackerEnv } from '@iv/shared/src/tracker';

export class CompsBridge {
  public static mainBridge: MainBridge;

  public static init() {
    if (CompsBridge.mainBridge) {
      return CompsBridge.mainBridge;
    }
    const mainBridge = new MainBridge();
    CompsBridge.mainBridge = mainBridge;
    return mainBridge;
  }

  public static destroy() {
    if (CompsBridge.mainBridge) {
      CompsBridge.mainBridge.destroy();
      CompsBridge.mainBridge = null;
    }
  }

  public utils: Utils;
  public nodeId: string;
  public isGlobal: boolean;

  // 每个组件都会分配一个CompsBridge实例，uid是组件的id
  private uid: string;

  private sounds: string[];

  public constructor({ uid, nodeId, isGlobal = false }: { uid: string; nodeId: string; isGlobal?: boolean }) {
    this.uid = uid;
    this.nodeId = nodeId;
    this.utils = new Utils(uid, this);
    this.sounds = [];
    this.isGlobal = isGlobal;
  }

  public invoke(ns: string, method: string, ...data: any) {
    return this.internalInvoke({ ns, method }, this.nodeId, data);
  }

  public forceInvoke(ns: string, method: string, ...data: any) {
    return this.internalInvoke({ ns, method }, '', data);
  }

  public on(ns: string, event: string, cb: (...args: any[]) => void) {
    if (ns === CompsNameSpace.utils) {
      this.utils.on(event, cb);
      return;
    }
    CompsBridge.mainBridge.startListen(ns, event);
    this.utils.on(`${ns}>${event}`, cb);
  }

  public once(ns: string, event: string, cb: (...args: any[]) => void) {
    CompsBridge.mainBridge.startListen(ns, event);
    this.utils.once(`${ns}>${event}`, cb);
  }

  public off(ns: string, event: string, cb?: (...args: any[]) => void) {
    this.utils.off(`${ns}>${event}`, cb);
  }

  public listenValue(key: string, cb: (...args: any[]) => void) {
    this.on(CompsNameSpace.utils, `valueChange${key}`, cb);
  }

  public listenCondition(condition: string, cb: (...args: any[]) => void) {
    this.utils.listenCondition(condition);
    this.on(CompsNameSpace.utils, `conditionChange${condition}`, cb);
  }

  public getValue(props: string) {
    return this.invoke(CompsNameSpace.utils, 'getValue', props);
  }

  public destroy() {
    this.utils.destroy();
    if (CompsBridge.mainBridge) {
      CompsBridge.mainBridge.removeBridge(this.uid);
    }
    // 释放音频资源
    this.sounds.forEach((soundId) => {
      this.forceInvoke(ChannelNameSpace.bridge, 'destroySound', soundId);
    });
  }

  private async internalInvoke(
    { ns, method }: { ns: string; method: string },
    nodeId: string,
    data: any[],
  ) {
    if (ns === CompsNameSpace.utils) {
      const caller: any = this.utils;
      if (!caller[method]) {
        console.warn(`utils.${method} not implement`);
        return;
      }
      return caller[method](...data);
    }
    let res = await CompsBridge.mainBridge.childBridge.invoke({
      nodeId,
      seq: `${Date.now()}${Math.floor(100000 * Math.random())}_${this.uid}`,
      ns,
      method,
      data,
    });

    res = this.invokeHook(ns, method, res);

    if (/([?&])invoke=1/.test(location.search)) {
      console.trace('[invoke log]', `ns=${ns}`, `method=${method}`, `nodeId=${nodeId}`, `data=${JSON.stringify(data)}`, `res=${JSON.stringify(res)}`);
    }

    return res;
  }

  private invokeHook(ns: string, method: string, res: any) {
    if (ns === ChannelNameSpace.bridge && method === 'loadSound') {
      const id = res?.result?.id;
      // 收集组件内产生的音频资源
      this.sounds.push(id);
    }
    return res;
  }
}

@classTracker(false, trackerEnv.child, ['createChildBridge'])
export class MainBridge extends CompsBridge {
  public childBridge: any;
  private bridgeMap: { [key: string]: CompsBridge };
  private asyncCallbacks: { [key: string]: Function[] };
  private listened: { [key: string]: boolean };

  /**
   * @constructor
   * @param {string} conf.origin 跨域通信信赖的origin
   */
  public constructor() {
    super({ uid: '0', nodeId: '' });
    this.bridgeMap = {};
    this.asyncCallbacks = {};
    this.listened = {};
    this.createChildBridge();
  }

  public startListen(ns: string, event: string) {
    const eventName = `${ns}>${event}`;
    if (this.listened[eventName]) {
      return;
    }
    this.listened[eventName] = true;
    this.childBridge.listen(ns, event, (data: any) => {
      this.utils.broadcast(eventName, data);
    });
  }

  public asyncEmit(event: string, data: any) {
    return Promise.all((this.asyncCallbacks[event] || []).map(callback => callback(data)));
  }

  public asyncOn(event: string, callback: Function) {
    if (!this.asyncCallbacks[event]) {
      this.asyncCallbacks[event] = [];
    }
    this.asyncCallbacks[event].push(callback);
  }

  /**
   * @description 组件获取独立的bridge实例
   * @param {string} uid
   * @returns
   */
  public getBridge(uid: string, isGlobal = false) {
    const bridge = new CompsBridge({
      uid,
      nodeId: this.nodeId,
      isGlobal,
    });
    this.bridgeMap[uid] = bridge;
    return bridge;
  }

  /**
   * @internal
   */
  public setCurrentNodeId(nodeId: string) {
    this.nodeId = nodeId;
    Object.keys(this.bridgeMap).forEach((uid) => {
      const bridge = this.bridgeMap[uid];
      // compsbridge 需是全局组件才需要更新其 nodeId
      if (bridge?.isGlobal) {
        bridge.nodeId = nodeId;
      }
    });
  }

  /**
   *
   * @description 移除组件bridge
   * @param {string} uid
   */
  public removeBridge(uid: string) {
    const bridge = this.bridgeMap[uid];
    if (bridge) {
      this.bridgeMap[uid] = null;
      if (bridge !== this) {
        if (bridge) {
          bridge.destroy();
        }
      }
    }
  }

  public destroy() {
    super.destroy();
    Object.keys(this.bridgeMap).forEach((uid) => {
      this.removeBridge(uid);
    });
    this.childBridge.destroy();
  }

  private createChildBridge() {
    this.childBridge = createChildBridge();
  }
}
